/** Pure helpers that turn GET /guilds/{id}/channels lists into a guild → category → channel tree. */
import type { ChannelNode, ChannelType, Guild } from "@/api/types";

export interface BuiltNode {
  id: string;
  kind: "guild" | "category" | "channel";
  name: string;
  guild?: Guild;
  channel?: ChannelNode;
  children: BuiltNode[];
}

/** Channel types that can be a mapping source. */
export const MAPPABLE_TYPES: ReadonlySet<ChannelType> = new Set(["text", "news", "forum", "thread"]);

export function isMappable(c: ChannelNode): boolean {
  return c.accessible && MAPPABLE_TYPES.has(c.type);
}

/**
 * Nest channels under their parents (categories, and threads under their channel).
 * Children whose parent is missing from the list are attached to the guild root, so a
 * filtered list that omits ancestors still renders.
 */
export function buildGuildTree(guild: Guild, channels: ChannelNode[]): BuiltNode {
  const nodes = new Map<string, BuiltNode>();
  for (const c of channels) {
    nodes.set(c.id, {
      id: c.id,
      kind: c.type === "category" ? "category" : "channel",
      name: c.name,
      channel: c,
      children: [],
    });
  }
  const root: BuiltNode = {
    id: `guild:${guild.id}`,
    kind: "guild",
    name: guild.name,
    guild,
    children: [],
  };
  for (const c of channels) {
    const node = nodes.get(c.id)!;
    const parent = c.parent_id ? nodes.get(c.parent_id) : undefined;
    (parent ?? root).children.push(node);
  }
  // Uncategorised channels first (Discord order), then categories.
  root.children.sort((a, b) => Number(a.kind === "category") - Number(b.kind === "category"));
  return root;
}

/** Every channel id (not categories) under a built node. */
export function channelIdsUnder(node: BuiltNode): string[] {
  const out: string[] = [];
  const walk = (n: BuiltNode) => {
    if (n.kind === "channel") out.push(n.id);
    n.children.forEach(walk);
  };
  walk(node);
  return out;
}

export type StatusFilter = "all" | "mapped" | "unmapped" | "inaccessible";

/** Status filter → GET /guilds/{id}/channels query params. */
export function statusQuery(status: StatusFilter): { mapped?: boolean; accessible?: boolean } {
  switch (status) {
    case "mapped":
      return { mapped: true };
    case "unmapped":
      return { mapped: false, accessible: true };
    case "inaccessible":
      return { accessible: false };
    default:
      return {};
  }
}
